import { useState, useEffect } from 'react'
import { Target, Loader2, HelpCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import { demoPosition } from '../utils/demoData'
import TrendModal from './TrendModal'
import TrendBadge from './TrendBadge'

// Tooltip component
const Tooltip = ({ text, children }: { text: string; children: React.ReactNode }) => {
  const [show, setShow] = useState(false)

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
            {text}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function PositionCard() {
  const { activeBrand } = useAuth()
  const { isDemoMode } = useDemoMode()
  const [loading, setLoading] = useState(true)
  const [allPositionData, setAllPositionData] = useState<any[]>([])
  const [showTrendModal, setShowTrendModal] = useState(false)

  useEffect(() => {
    fetchPosition()
  }, [activeBrand?.id, isDemoMode])

  const fetchPosition = async () => {
    if (!activeBrand?.id && !isDemoMode) return

    try {
      setLoading(true)

      // Use demo data if in demo mode
      if (isDemoMode) {
        setAllPositionData(demoPosition.data)
        setLoading(false)
        return
      }

      const response = await api.get(`/competitors/${activeBrand!.id}/position`)

      if (response.data.success && response.data.data) {
        setAllPositionData(response.data.data)
      }
    } catch (error) {
      console.error('Failed to fetch Position:', error)
    } finally {
      setLoading(false)
    }
  }

  const getPositionScore = (avgPosition: number) => {
    if (avgPosition <= 1) return { score: 20, label: 'Excellent', color: 'text-green-600' }
    if (avgPosition <= 2) return { score: 18, label: 'Very Good', color: 'text-green-500' }
    if (avgPosition <= 3) return { score: 15, label: 'Good', color: 'text-blue-600' }
    if (avgPosition <= 5) return { score: 10, label: 'Fair', color: 'text-yellow-600' }
    if (avgPosition <= 7) return { score: 5, label: 'Needs Work', color: 'text-orange-600' }
    return { score: 0, label: 'Poor', color: 'text-red-600' }
  }

  const getPositionLabel = (avgPosition: number) => {
    if (avgPosition <= 1) return '1st sentence'
    if (avgPosition <= 2) return '2nd sentence'
    if (avgPosition <= 3) return '3rd sentence'
    if (avgPosition <= 5) return 'Mid-response'
    if (avgPosition <= 7) return 'Late response'
    return 'Buried'
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Target className="h-5 w-5 text-indigo-600" />
            <h3 className="text-sm font-medium text-gray-600">Position & Ranking</h3>
          </div>
        </div>
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
        </div>
      </div>
    )
  }

  // Check if there's any actual data (any brand with mentions)
  const hasData = allPositionData.length > 0 && allPositionData.some((brand: any) => brand.totalMentions > 0)

  // Get brands with data, sorted by position (lower is better) - needed for trendData
  const brandsWithData = allPositionData
    .filter((brand: any) => brand.totalMentions > 0 && brand.avgPosition !== null)
    .sort((a: any, b: any) => {
      // Your brand always comes first
      if (a.isYourBrand) return -1
      if (b.isYourBrand) return 1
      // Otherwise sort by position (lower position = better ranking)
      return a.avgPosition - b.avgPosition
    })

  // Get your brand's data (may not have mentions)
  const yourBrand = allPositionData.find((b: any) => b.isYourBrand)

  // Get the top brand for main display
  const topBrand = (yourBrand && yourBrand.totalMentions > 0) ? yourBrand : brandsWithData[0]

  const avgPosition = topBrand?.avgPosition || 0

  // Trend data (6 months) - only use demo values in demo mode (lower is better)
  const startingPosition = 3.8
  const trendData = isDemoMode ? [
    { date: new Date(Date.now() - 5 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: startingPosition },
    { date: new Date(Date.now() - 4 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 3.5 },
    { date: new Date(Date.now() - 3 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 3.2 },
    { date: new Date(Date.now() - 2 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 2.9 },
    { date: new Date(Date.now() - 1 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 2.6 },
    { date: new Date(Date.now()).toISOString(), value: avgPosition || 2.3 }
  ] : [
    { date: new Date(Date.now() - 5 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now() - 4 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now() - 3 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now() - 2 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now() - 1 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now()).toISOString(), value: avgPosition }
  ]

  if (!hasData) {
    return (
      <>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-gray-400" />
              <h3 className="text-sm font-medium text-gray-600">Position & Ranking</h3>
            </div>
            <TrendBadge
              hasHistoricalData={true}
              trend="stable"
              value={0}
              unit=" pos"
              decimals={1}
              onClick={() => setShowTrendModal(true)}
            />
          </div>
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">No mentions found</p>
            <p className="text-xs text-gray-400 mt-2">
              Your brand and your competitors were not found in the searched queries.
            </p>
          </div>
        </div>

        {/* Trend Modal */}
        <TrendModal
          isOpen={showTrendModal}
          onClose={() => setShowTrendModal(false)}
          title="Position Ranking Trend"
          metricName="Average Position"
          data={trendData}
          color="#3b82f6"
          unit=""
        />
      </>
    )
  }

  const positionInfo = getPositionScore(avgPosition)

  // Badge shows month-over-month change (current vs last month)
  const lastMonthPosition = trendData[trendData.length - 2]?.value || avgPosition
  const monthOverMonthChange = avgPosition - lastMonthPosition

  // Trend calculation based on month-over-month (lower position is better)
  const trend = avgPosition < lastMonthPosition ? 'up' : avgPosition > lastMonthPosition ? 'down' : 'stable'

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Target className="h-5 w-5 text-indigo-600" />
            <h3 className="text-sm font-medium text-gray-600">Position & Ranking</h3>
            <Tooltip text="Shows where your brand appears in AI responses. Lower positions (1-3) mean your brand is mentioned earlier, which indicates stronger visibility.">
              <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </Tooltip>
          </div>
          <TrendBadge
            hasHistoricalData={true}
            trend={trend}
            value={Math.abs(monthOverMonthChange)}
            unit=" pos"
            decimals={1}
            onClick={() => setShowTrendModal(true)}
          />
        </div>

        <div className="mb-4">
          {yourBrand && yourBrand.totalMentions === 0 ? (
            // Your brand has no mentions - show N/A
            <>
              <div className="flex items-baseline space-x-2">
                <p className="text-3xl font-bold text-gray-400">N/A</p>
              </div>
              <p className="text-sm font-medium text-gray-600 mt-1">
                No position data
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Your brand • 0 mentions
              </p>
            </>
          ) : (
            // Show actual position data
            <>
              <div className="flex items-baseline space-x-2">
                <p className={`text-3xl font-bold ${positionInfo.color}`}>
                  #{avgPosition.toFixed(1)}
                </p>
              </div>
              <p className="text-sm font-medium text-gray-600 mt-1">
                {getPositionLabel(avgPosition)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {topBrand.isYourBrand ? 'Your brand' : topBrand.name} • {topBrand.totalMentions} mentions
              </p>
            </>
          )}
        </div>

        {/* Competitor Position List */}
        <div className="space-y-3">
          {brandsWithData.slice(0, 3).map((brand: any) => {
            const brandPositionInfo = getPositionScore(brand.avgPosition)

            return (
              <div key={brand.id} className="border-t border-gray-100 pt-3 first:border-t-0 first:pt-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${brand.isYourBrand ? 'text-gray-900' : 'text-gray-600'}`}>
                      {brand.name}
                      {brand.isYourBrand && <span className="ml-1 text-indigo-600 text-xs">(You)</span>}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-lg font-bold ${brandPositionInfo.color}`}>
                      #{brand.avgPosition.toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-400">{brand.totalMentions}m</span>
                  </div>
                </div>

                {/* Position bar */}
                <div className="relative">
                  <div className="w-full bg-gradient-to-r from-green-200 via-yellow-200 to-red-200 rounded-full h-2">
                    {/* Position marker */}
                    <div
                      className="absolute top-0 transform -translate-x-1/2 -translate-y-0.5 transition-all"
                      style={{ left: `${Math.min(100, (brand.avgPosition / 8) * 100)}%` }}
                    >
                      <div className={`w-3 h-3 rounded-full border-2 border-white shadow-sm ${
                        brandPositionInfo.score >= 15 ? 'bg-green-600' :
                        brandPositionInfo.score >= 10 ? 'bg-yellow-600' : 'bg-red-600'
                      }`} />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{getPositionLabel(brand.avgPosition)}</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Trend Modal */}
      <TrendModal
        isOpen={showTrendModal}
        onClose={() => setShowTrendModal(false)}
        title="Position Ranking Trend"
        metricName="Average Position"
        data={trendData}
        color="#3b82f6"
        unit=""
      />
    </div>
  )
}
